import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Card } from '@/components/ui/card';
import { useABTestWizardStore } from '@/store/ab-test/wizardStore';
import { useABTestStore } from '@/store/ab-test/abTestStore';
import { useAuth } from '@/hooks/useAuth';
import { HookTrackWizardStepper } from '@/components/hook-track/wizard/HookTrackWizardStepper';
import { HookTrackWizardFooter } from '@/components/hook-track/wizard/HookTrackWizardFooter';
import { VideosSelectionStep } from '@/components/ab-test/wizard/steps/VideosSelectionStep';
import { ABTestTrafficDistributionStep } from '@/components/ab-test/wizard/steps/ABTestTrafficDistributionStep';
import { ABTestNameModal } from '@/components/ab-test/wizard/ABTestNameModal';
import { useToast } from '@/hooks/use-toast';

export default function ABTestCreate() {
  useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { createTest } = useABTestStore();
  const {
    currentStep,
    steps,
    setCurrentStep,
    nextStep,
    prevStep,
    validateCurrentStep,
    completeStep,
    data,
    setTestInfo,
    reset
  } = useABTestWizardStore();

  const [isFinishing, setIsFinishing] = React.useState(false);
  const [showNameModal, setShowNameModal] = React.useState(!data.testName);

  const handleNext = () => {
    if (validateCurrentStep()) {
      completeStep(currentStep);
      nextStep();
    }
  };

  const handleFinish = async () => {
    if (!validateCurrentStep()) {
      toast({
        title: translateInterfaceText("Configuração incompleta"),
        description: translateInterfaceText("Por favor, verifique se a distribuição de tráfego soma 100%"),
        variant: "destructive"
      });
      return;
    }

    if (!user?.id_user) {
      toast({
        title: translateInterfaceText("Erro de autenticação"),
        description: translateInterfaceText("Não foi possível identificar o usuário. Faça login novamente."),
        variant: "destructive"
      });
      return;
    }

    setIsFinishing(true);

    try {
      // Preparar dados para enviar ao backend
      const videosIds = data.selectedVideos.map(video => ({
        id_video: video.id,
        trafego: data.trafficDistribution.percentages[video.id] || 0,
      }));

      const payload = {
        name: data.testName,
        id_user: user.id_user,
        videosIds,
        copy: false, // Pode ser configurável no futuro
      };

      // Chamar API para criar teste
      const newTest = await createTest(payload);

      if (newTest) {
        toast({
          title: translateInterfaceText("Teste criado com sucesso!"),
          description: translateInterfaceText("Seu teste A/B está ativo e coletando dados."),
        });

        // Navigate to test list
        navigate('/ab-test/list');

        // Reset wizard after navigation
        setTimeout(() => reset(), 100);
      } else {
        throw new Error(translateInterfaceText("Falha ao criar teste"));
      }
    } catch (error: any) {
      console.error('Erro ao criar teste A/B:', error);
      toast({
        title: translateInterfaceText("Erro ao criar teste"),
        description: error?.message || translateInterfaceText("Ocorreu um erro ao criar o teste. Tente novamente."),
        variant: "destructive"
      });
    } finally {
      setIsFinishing(false);
    }
  };

  const handleExit = () => {
    if (window.confirm(translateInterfaceText("Deseja sair? As alterações não salvas serão perdidas."))) {
      reset();
      navigate('/ab-test/list');
    }
  };

  const handleTestNameConfirm = (name: string, description: string) => {
    setTestInfo(name, description);
    setShowNameModal(false);
  };

  const handleTestNameCancel = () => {
    navigate('/ab-test/list');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <VideosSelectionStep />;
      case 1:
        return <ABTestTrafficDistributionStep />;
      default:
        return null;
    }
  };

  // Map steps for stepper component
  const updatedSteps = steps.map((step, index) => ({
    id: index,
    title: step.title,
    description: step.description,
    completed: step.isCompleted
  }));

  // Debug
  console.log('Current Step:', currentStep);

  return (
    <Layout fixedSidebar>
      <ABTestNameModal
        open={showNameModal}
        onConfirm={handleTestNameConfirm}
        onCancel={handleTestNameCancel}
      />

      <div className="flex flex-col h-[100dvh] lg:h-screen bg-white overflow-hidden">
        {/* Header de Contexto do Experimento */}
        <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-6 pb-3 bg-white z-10">
          <h2 className="text-[18px] lg:text-[20px] font-semibold text-[#1A1A1A]">
            {currentStep === 0 ? translateInterfaceText("Seleção de vídeos") : translateInterfaceText("Distribuição de Tráfego")}
          </h2>
        </div>

        <div className="h-px w-full bg-border" />

        {/* Stepper - Fixed */}
        <div className="flex-shrink-0">
          <HookTrackWizardStepper
            steps={updatedSteps}
            currentStep={currentStep}
            onStepClick={setCurrentStep}
          />
        </div>

        {/* Conteúdo - Área principal com scroll interno gerenciado pelo step */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pb-24">
          <div className="max-w-[1440px] mx-auto">
            {renderStepContent()}
          </div>
        </div>

        {/* Footer - Fixed (position: fixed no componente) */}
        <HookTrackWizardFooter
            currentStep={currentStep}
            totalSteps={steps.length}
            canGoNext={validateCurrentStep()}
            onBack={prevStep}
            onNext={handleNext}
            onFinish={handleFinish}
            isFinishing={isFinishing}
            onExit={handleExit}
          />
      </div>
    </Layout>
  );
}
